import {
  SHAutocomplete,
  SHBox,
  SHChip,
  SHDivider,
  SHIconLoading,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { CloudDownloadSVG } from "@components/svgs/cloud-download";
import { RecycleBinSVG } from "@components/svgs/recycle-bin";
import { DELAY_TIME } from "@constants";
import { useClickAwayRefs } from "@hooks/useClickAwayRefs";
import { ModelScreenerColumnSettingDTO } from "@models/model-screener/entities/modelScreenerFilter";
import BookmarkAddedOutlinedIcon from "@mui/icons-material/BookmarkAddedOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { IconButton, Tooltip, tooltipClasses, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useToggle } from "react-use";

export interface MSColumnDropdownButtonProps {
  buttonTitle: string | undefined;
  options: ModelScreenerColumnSettingDTO[];
  onSelectOption: (option: ModelScreenerColumnSettingDTO) => Promise<boolean>;
  onDeleteOption?: (option: ModelScreenerColumnSettingDTO) => Promise<void>;
  onMakeDefaultOption: (
    option: ModelScreenerColumnSettingDTO,
  ) => Promise<boolean>;
}

export const MSColumnDropdownButton = ({
  buttonTitle,
  options,
  onSelectOption,
  onDeleteOption,
  onMakeDefaultOption,
}: MSColumnDropdownButtonProps) => {
  const { palette, shadows } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] =
    useState<ModelScreenerColumnSettingDTO>();
  const [isLoading, toggleLoading] = useToggle(false);
  const [deletingOptionId, setDeletingOptionId] = useState<string | null>(null);
  const [makingDefaultOptionId, setMakingDefaultOptionId] = useState<
    string | null
  >(null);

  const { watch } = useFormContext<ModelScreenerColumnSettingDTO>();
  const currentFilterId = watch("id");

  useEffect(() => {
    const selectedOption = options?.find(
      (option) => option.id === currentFilterId,
    );
    setSelectedOption(selectedOption);
  }, [currentFilterId, options]);

  const isSelected = (option: ModelScreenerColumnSettingDTO) => {
    return selectedOption?.id === option.id;
  };

  const isDeleting = (option: ModelScreenerColumnSettingDTO) => {
    return (
      !option.isSystemDefault &&
      !option.isUserDefault &&
      option.id === deletingOptionId
    );
  };

  const isMakingDefault = (option: ModelScreenerColumnSettingDTO) => {
    return !option.isUserDefault && option.id === makingDefaultOptionId;
  };

  const handleSelectOption = async (option: ModelScreenerColumnSettingDTO) => {
    try {
      if (isDeleting(option)) return;
      setIsOpen(false);
      await onSelectOption(option);
    } finally {
      setTimeout(() => toggleLoading(false), 300);
    }
  };

  const handleOnDeleteOption = async (
    option: ModelScreenerColumnSettingDTO,
  ) => {
    onDeleteOption && (await onDeleteOption(option));
    setDeletingOptionId(null);
  };

  const handleOnMakeDefaultOption = async (
    option: ModelScreenerColumnSettingDTO,
  ) => {
    await onMakeDefaultOption(option);
    setMakingDefaultOptionId(null);
  };

  useClickAwayRefs([containerRef, autocompleteRef], () => {
    setIsOpen(false);
  });

  return (
    <SHStack
      ref={containerRef}
      direction={"row"}
      border={"1px solid"}
      borderRadius={"30px"}
      width={260}
      padding={"5px 15px 5px"}
      alignItems={"center"}
      height={32}
      gap={0.5}
      sx={{
        transition: "0.2s ease-in-out",
        ":active": {
          bgcolor: palette.primary.main,
          "p, svg, .MuiInput-input": {
            color: `${palette.common.white} !important`,
          },
        },
        ":hover": {
          borderColor: palette.primary.main,
        },
        svg: {
          color: isLoading ? palette.text.disabled : palette.text.primary,
        },
      }}
      onClick={() => setIsOpen(true)}
    >
      <SHStack width={18} height={18}>
        <CloudDownloadSVG width={18} height={18} />
      </SHStack>
      <SHStack width={"100%"} height={"23px"}>
        <SHTypography
          disabled={isLoading}
          fontSize={"9px"}
          lineHeight={1}
          color={palette.grey[500]}
          textAlign={"left"}
          paddingTop={0}
        >
          {buttonTitle}
        </SHTypography>
        <SHAutocomplete
          key={`${selectedOption?.id}`}
          sx={{
            justifyContent: "space-between",
            "& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline.Mui-disabled:before":
              {
                borderBottom: "none",
              },
            "& .MuiInput-input": {
              padding: "0 !important",
              fontSize: "11px",
              fontWeight: 500,
            },
            "& .MuiInput-root": {
              marginTop: "0px !important",
              height: "15px !important",
              paddingRight: "0px !important",
            },
            "& .MuiAutocomplete-endAdornment": {
              display: "none !important",
            },
          }}
          disabled={isLoading}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          popupIcon={null}
          value={selectedOption}
          options={options}
          onChange={(_, value) => {
            if (!value) return;
            setSelectedOption(value);
          }}
          getOptionLabel={(option) => option.name ?? ""}
          renderOption={(_, option) => {
            const shouldShowDefaultChip = option.isUserDefault;
            const shouldShowLoadingIcon =
              isDeleting(option) || isMakingDefault(option);
            const shouldShowCheckIcon =
              isSelected(option) && !shouldShowLoadingIcon;
            const shouldShowMakeDefaultIcon =
              !shouldShowLoadingIcon && !shouldShowDefaultChip;
            const shouldShowBinIcon =
              shouldShowMakeDefaultIcon &&
              !shouldShowCheckIcon &&
              !option.isSystemDefault;
            return (
              <SHStack
                ref={wrapperRef}
                gap="5px"
              >
                <SHStack
                  key={option.id}
                  minWidth={"300px"}
                  direction="row"
                  paddingX="12px"
                  paddingY="7px"
                  bgcolor={
                    isSelected(option) ? "#F2F1FF" : palette.common.white
                  }
                  borderRadius="8px"
                  sx={{
                    cursor: "pointer",
                    ":hover": {
                      backgroundColor: "#F2F1FF",
                    },
                  }}
                  alignItems="center"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLoading(true);
                    setTimeout(() => handleSelectOption(option), 0);
                  }}
                  gap="15px"
                  justifyContent={"space-between"}
                >
                  <SHTypography
                    minWidth={100}
                    maxWidth={250}
                    fontSize="12px"
                    lineHeight="22px"
                    disabled={isLoading}
                  >
                    {option.name}
                  </SHTypography>

                  <SHStack direction={"row"} gap={1}>
                    {shouldShowDefaultChip && (
                      <SHStack width="100px">
                        <SHChip
                          icon={
                            <BookmarkAddedOutlinedIcon
                              sx={{ color: "white !important" }}
                            />
                          }
                          label={"Default"}
                          variant="filled"
                          size="small"
                          sx={{
                            backgroundColor: palette.primary.main,
                            color: palette.common.white,
                            fontSize: "12px",
                          }}
                          disabled={isLoading}
                        />
                      </SHStack>
                    )}

                    {shouldShowLoadingIcon && (
                      <SHIconLoading
                        size={20}
                        sx={{
                          color: palette.text.disabled,
                        }}
                      />
                    )}

                    {shouldShowMakeDefaultIcon && (
                      <Tooltip
                        placement="bottom"
                        enterDelay={DELAY_TIME}
                        enterNextDelay={DELAY_TIME}
                        slotProps={{
                          popper: {
                            sx: {
                              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                {
                                  marginTop: "6px",
                                },
                            },
                          },
                        }}
                        title={
                          <SHStack
                            sx={{
                              ".MuiTypography-root": {
                                color: palette.common.white,
                              },
                            }}
                          >
                            <SHTypography fontSize="12px" disabled={isLoading}>
                              Set view as default
                            </SHTypography>
                          </SHStack>
                        }
                      >
                        <SHBox component="span">
                          <IconButton
                            size="small"
                            sx={{ padding: 0 }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setMakingDefaultOptionId(option.id);
                              await handleOnMakeDefaultOption(option);
                            }}
                          >
                            <BookmarkBorderOutlinedIcon
                              sx={{
                                color: isLoading
                                  ? palette.text.disabled
                                  : palette.common.black,
                                width: "20px",
                                ":hover": {
                                  color: palette.primary.main,
                                },
                              }}
                            />
                          </IconButton>
                        </SHBox>
                      </Tooltip>
                    )}

                    {shouldShowCheckIcon && (
                      <Tooltip
                        placement="bottom"
                        enterDelay={DELAY_TIME}
                        enterNextDelay={DELAY_TIME}
                        slotProps={{
                          popper: {
                            sx: {
                              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                {
                                  marginTop: "6px",
                                },
                            },
                          },
                        }}
                        title={
                          <SHStack
                            sx={{
                              ".MuiTypography-root": {
                                color: palette.common.white,
                              },
                            }}
                          >
                            <SHTypography fontSize="12px" disabled={isLoading}>
                              Selected view
                            </SHTypography>
                          </SHStack>
                        }
                      >
                        <CheckRoundedIcon
                          sx={{
                            width: "20px",
                          }}
                          color={isLoading ? "disabled" : "primary"}
                        />
                      </Tooltip>
                    )}

                    {shouldShowBinIcon && (
                      <Tooltip
                        placement="bottom"
                        enterDelay={DELAY_TIME}
                        enterNextDelay={DELAY_TIME}
                        slotProps={{
                          popper: {
                            sx: {
                              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                {
                                  marginTop: "6px",
                                },
                            },
                          },
                        }}
                        title={
                          <SHStack
                            sx={{
                              ".MuiTypography-root": {
                                color: palette.common.white,
                              },
                            }}
                          >
                            <SHTypography fontSize="12px">
                              Delete template
                            </SHTypography>
                          </SHStack>
                        }
                      >
                        <SHBox component={"span"}>
                          <IconButton
                            size="small"
                            sx={{
                              height: "100%",
                              padding: 0,
                              "&:hover": {
                                "& svg path": {
                                  fill: palette.error.main,
                                  transition: "all 0.3s ease",
                                },
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingOptionId(option.id);
                              setTimeout(
                                async () => await handleOnDeleteOption(option),
                                0,
                              );
                            }}
                          >
                            <RecycleBinSVG
                              color={
                                isLoading
                                  ? palette.text.disabled
                                  : palette.common.black
                              }
                              width={20}
                            />
                          </IconButton>
                        </SHBox>
                      </Tooltip>
                    )}
                  </SHStack>
                </SHStack>
                {shouldShowDefaultChip && options.length > 2 && (
                  <SHDivider
                    orientation="horizontal"
                    sx={{ width: "100%", borderColor: palette.text.disabled }}
                  />
                )}
              </SHStack>
            );
          }}
          componentsProps={{
            paper: {
              sx: {
                borderRadius: "8px !important",
                padding: "12px",
                marginTop: "3px !important",
                ul: {
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                },
              },
            },
          }}
          textFieldProps={{
            maxLength: 50,
          }}
          noOptionsText={
            <SHStack
              alignItems={"center"}
              justifyContent={"center"}
              height={16}
            >
              <SHTypography fontSize={13} colorVariant="disabled">
                No options
              </SHTypography>
            </SHStack>
          }
          PaperComponent={(props) => (
            <SHStack
              {...props}
              marginLeft={"-38px !important"}
              boxShadow={shadows[2]}
              bgcolor={palette.common.white}
              sx={{
                width: "350px !important",
                "& .MuiAutocomplete-noOptions": {
                  padding: "0px !important",
                },
              }}
              ref={autocompleteRef}
            >
              {props.children}
            </SHStack>
          )}
        />
      </SHStack>
      <SHStack
        width={20}
        height={20}
        alignItems="center"
        justifyContent="center"
      >
        {isLoading ? (
          <SHIconLoading
            size={20}
            sx={{
              color: palette.text.disabled,
            }}
          />
        ) : (
          <SHStack
            sx={{
              transition: "all 0.3s",
              transform: isOpen ? "rotate(-90deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreRoundedIcon />
          </SHStack>
        )}
      </SHStack>
    </SHStack>
  );
};
